/* eslint-disable @typescript-eslint/no-unused-vars */
import api from 'api/axiosInstance';
import { createApiUrl, getToken } from '../../../util';
import {
	CreateEditPayload,
	EditDSMPayload,
	EditType,
	FlattenAreaWireShape,
	UpdateEditPayload,
} from 'components/Tree/type';
import { useMutation } from 'react-query';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { EditDsmResponse } from 'api/editDsm';
import { getTrunkHeight, Tree, type FlattenShape } from 'store/slices/TreeSlice';
import { selectTreeState } from 'store/slices/TreeSlice/selector';

const normalizeFlattenShapeForApi = (shape?: FlattenShape): FlattenAreaWireShape | undefined => {
	if (shape === undefined) return undefined;
	return shape === 'circle' ? FlattenAreaWireShape.Circle : FlattenAreaWireShape.Polygon;
};

export function useEditDSM() {
	const { trees, flattenArea, deleted } = useSelector((state: RootState) => state.treeSlice.data);
	const { trees: mergedTrees } = useSelector(selectTreeState);
	const { uuid, groundMetersPerPixel } = useSelector((state: RootState) => state.roofData.data);

	const mergedTreesById = new Map(mergedTrees.map((tree) => [String(tree.id), tree]));

	const create: CreateEditPayload[] = [
		...Object.values(trees.added).map((tree) => {
			const { id, treeHeight, crownHeight, crownRadius, trunkRadius, ...rest } = tree;
			return {
				type: EditType.TREE as const,
				edits: {
					...rest,
					crownHeight,
					trunkHeight: getTrunkHeight(tree),
					crownRadius: crownRadius * groundMetersPerPixel,
					trunkRadius: trunkRadius * groundMetersPerPixel,
				},
			};
		}),

		...Object.values(flattenArea.added).map((area) => {
			const { id, flat_px, facet_affected_px, ground_z, shape, ...rest } = area;
			return {
				type: EditType.FLATTEN_AREA as const,
				edits: { ...rest, shape: normalizeFlattenShapeForApi(shape) },
			};
		}),
	];

	const update: UpdateEditPayload[] = [
		...Object.entries(trees.updated).map(([id, treeEdits]) => {
			const edits: Partial<TreeType> = {};
			(Object.keys(treeEdits) as (keyof Tree)[]).forEach((key) => {
				if (key === 'treeHeight') return; // converted to trunkHeight below
				if (key === 'crownRadius') {
					edits.crownRadius = treeEdits.crownRadius! * groundMetersPerPixel;
					return;
				}
				if (key === 'trunkRadius') {
					edits.trunkRadius = treeEdits.trunkRadius! * groundMetersPerPixel;
					return;
				}
				(edits as Record<string, unknown>)[key] = treeEdits[key];
			});

			// trunkHeight is derived from the full (merged) tree, since it
			// depends on treeHeight and crownHeight together and this update
			// may only carry one of the two.
			if ('treeHeight' in treeEdits || 'crownHeight' in treeEdits) {
				const fullTree = mergedTreesById.get(id);
				if (fullTree) {
					edits.trunkHeight = getTrunkHeight(fullTree);
				}
			}

			return {
				id: Number(id),
				type: EditType.TREE as const,
				edits,
			};

		}),

		...Object.entries(flattenArea.updated).map(([id, area]) => {
			const { shape, ...edits } = area;
			return {
				id: Number(id),
				type: EditType.FLATTEN_AREA as const,
				edits: { ...edits, shape: normalizeFlattenShapeForApi(shape) },
			};
		}),
	];

	const del = deleted
		.filter((id): id is number => typeof id === 'number');

	const payload: EditDSMPayload = {
		uuid,
		...(create.length > 0 && { create }),
		...(update.length > 0 && { update }),
		...(del.length > 0 && { delete: del }),
	};

	return useMutation({
		mutationKey: `edit-dsm-${uuid}`,
		mutationFn: () => editDsm(payload),
		onSuccess: (data) => {
			//
		},
		onError: (err) => {
			console.error('Failed to update DSM:', err);
		},
	});
}

export async function editDsm(payload: EditDSMPayload): Promise<EditDsmResponse> {
	const token = getToken('Authorization');

	const { data } = await api.post(
		createApiUrl('/designs/edit-dsm'),
		payload,
		{
			headers: {
				Authorization: `Bearer ${token}`,
			},
		}
	);

	return data;
}
